import React, { useEffect, useState } from "react";

import { CalendarDays } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../intelligence-briefing-library/components/ui/tooltip";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchAssessmentCalendarStatus } from "../../Store/Reducers/AssessmentSlice";

type CalendarWindow = {
  open_date: string;
  due_date: string;
  publish_date: string;
};

type CalendarStatus = {
  state: "window_open" | "window_closed" | "remainder";
  today: string;
  cadence_days: number;
  window_days: number;
  window_offset_days: number | null;
  current_window:
    | (CalendarWindow & {
        days_left: number;
        accounts_total: number;
        accounts_open: number;
        closed_on: string | null;
      })
    | null;
  next_window: CalendarWindow;
  private_window: { start_date: string; end_date: string } | null;
  counts_on: string;
};

const formatDay = (iso?: string | null, withYear = false): string => {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
};

const STATE_STYLES: Record<CalendarStatus["state"], string> = {
  window_open: "border-green-200 bg-green-50 text-green-800",
  window_closed: "border-blue-200 bg-blue-50 text-blue-800",
  remainder: "border-amber-200 bg-amber-50 text-amber-800",
};

const DOT_STYLES: Record<CalendarStatus["state"], string> = {
  window_open: "bg-green-500",
  window_closed: "bg-blue-500",
  remainder: "bg-amber-500",
};

const TOOLTIP_CLASS =
  "z-[9999] w-[300px] border border-gray-200 bg-white p-0 font-normal normal-case tracking-normal text-gray-800 shadow-[0_4px_16px_rgba(0,0,0,0.12)]";

type CalendarHint = {
  heading: string;
  description: string;
  rows: { label: string; value: string; personal?: boolean }[];
  note: string;
};

const describe = (
  status: CalendarStatus
): { title: string; detail: string; hint: CalendarHint } => {
  const current = status.current_window;
  const nextOpen = formatDay(status.next_window.open_date, true);
  const cycleNote = `Assessments run every ${status.cadence_days} days, starting with a Team window.`;
  if (status.state === "window_open" && current) {
    const daysLeft = current.days_left;
    const left =
      daysLeft <= 0
        ? "closes today"
        : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
    return {
      title: "Team window open",
      detail: `${formatDay(current.open_date)} – ${formatDay(current.due_date)} · publishes ${formatDay(current.publish_date)} · ${left}`,
      hint: {
        heading: "Team window is open",
        description:
          "Everyone on the account gives their level now. Levels stay hidden until results are published.",
        rows: [
          {
            label: "Give your input by",
            value: formatDay(current.due_date, true),
            personal: true,
          },
          {
            label: "Results published",
            value: formatDay(current.publish_date, true),
          },
          { label: "Next Team window", value: nextOpen },
        ],
        note: cycleNote,
      },
    };
  }
  const privateHint: CalendarHint = {
    heading: "Private window",
    description:
      "You can still give your level. Only you, ESLs and CXOs can see it.",
    rows: [
      {
        label: "Your input counts on",
        value: formatDay(status.counts_on, true),
        personal: true,
      },
      { label: "Next Team window", value: nextOpen },
    ],
    note: `Your newest input replaces older ones. ${cycleNote}`,
  };
  if (status.state === "window_closed" && current) {
    return {
      title: "Window closed",
      detail: `Published ${formatDay(current.closed_on)} · new inputs count on ${formatDay(status.counts_on, true)}`,
      hint: privateHint,
    };
  }
  const privateWindow = status.private_window;
  const range = privateWindow
    ? `${formatDay(privateWindow.start_date)} – ${formatDay(privateWindow.end_date)} · `
    : "";
  return {
    title: "Private window open",
    detail: `${range}Next window opens ${nextOpen}`,
    hint: privateHint,
  };
};

type AssessmentCalendarStatusBadgeProps = {
  /** Show the viewer's own input dates in the tooltip (My Assessments). */
  showPersonalDates?: boolean;
};

const AssessmentCalendarStatusBadge: React.FC<
  AssessmentCalendarStatusBadgeProps
> = ({ showPersonalDates = true }) => {
  const dispatch = useAppDispatch();
  const [status, setStatus] = useState<CalendarStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await dispatch(
          fetchAssessmentCalendarStatus()
        ).unwrap()) as { content?: CalendarStatus };
        if (!cancelled && res?.content?.state) {
          setStatus(res.content);
        }
      } catch {
        if (!cancelled) setStatus(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!status) return null;

  const { title, detail, hint } = describe(status);
  const rows = hint.rows.filter(row => showPersonalDates || !row.personal);

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className={`inline-flex w-fit cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs font-normal leading-normal ${STATE_STYLES[status.state]}`}
          >
            <span
              className={`h-2 w-2 rounded-full ${DOT_STYLES[status.state]}`}
            />
            <CalendarDays size={14} />
            <span className="font-semibold">{title}</span>
            <span className="opacity-80">· {detail}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          sideOffset={8}
          className={TOOLTIP_CLASS}
        >
          <div className="px-3.5 pt-3 pb-2.5">
            <p className="mb-1 text-[13px] font-semibold text-gray-900">
              {hint.heading}
            </p>
            <p className="mb-0 text-[12.5px] leading-snug text-gray-600">
              {hint.description}
            </p>
          </div>
          <dl className="m-0 border-t border-gray-100 px-3.5 py-2.5">
            {rows.map(row => (
              <div
                key={row.label}
                className="flex items-center justify-between gap-4 py-0.5 text-[12.5px]"
              >
                <dt className="text-gray-500">{row.label}</dt>
                <dd className="m-0 font-semibold text-gray-900">{row.value}</dd>
              </div>
            ))}
          </dl>
          <p className="mb-0 rounded-b-md border-t border-gray-100 bg-gray-50 px-3.5 py-2 text-[11.5px] leading-snug text-gray-500">
            {hint.note}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default AssessmentCalendarStatusBadge;
